"use client";

import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { zodResolver } from "@hookform/resolvers/zod";
import { useUser } from "hooks/useUser";
import { useWorkspaces } from "hooks/useWorkspaces";

import { Input } from "components/Input";
import { LinearButton } from "components/LinearButton";

import loginLogo from "assets/login-logo.png";
import { BsExclamationCircleFill } from "react-icons/bs";
import { getSafePostLoginPath } from "utils/authRoutes";

const loginFormSchema = z.object({
  email: z.string(),
  password: z.string(),
});

type loginFormInputs = z.infer<typeof loginFormSchema>;

export default function LoginComponent() {
  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<loginFormInputs>({
    resolver: zodResolver(loginFormSchema),
  });
  const { login, authUser } = useUser();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { checkIfEmailExists } = useWorkspaces();
  const [errorMessage, setErrorMessage] = useState<string>("");

  async function handleLogin(data: loginFormInputs) {
    setErrorMessage("");

    try {
      const response = await login(data);
      await checkIfEmailExists(data.email);
      if (response) {
        router.push(
          getSafePostLoginPath(searchParams.get("redirect"), response),
        );
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.content?.message ||
        "These credentials do not match our records.";
      setErrorMessage(message);
    }
  }

  return (
    <div className="flex flex-col justify-center items-center h-full text-azulfy-rich-black max-md:pb-20 px-10 max-h-screen overflow-y-auto">
      <div className="w-[100px] max-md:w-[75px] relative h-[75px]">
        <Image
          src={loginLogo}
          alt="Azulfy logo"
          fill
          className="object-contain"
        />
      </div>

      <span className="font-raleway text-lg hidden md:block mt-3">
        Let&#8217;s Azulfy the world together.
      </span>
      <form
        onSubmit={handleSubmit(handleLogin)}
        className="flex flex-col gap-12 md:mt-20 max-md:mt-12 w-full max-w-[400px]"
      >
        <div className="flex flex-col">
          <h1 className="text-5xl max-md:text-3xl text-azulfy-rich-black mb-2 text-center md:min-w-[350px]">
            {authUser && authUser?.isAuthenticated === false
              ? "Welcome Back!"
              : "Welcome!"}
          </h1>
          <span className="text-center font-comfortaa text-azulfy-rich-black">
            Login to your account
          </span>
        </div>

        {errorMessage && (
          <div
            className="flex items-center gap-3 px-4 py-3 bg-error/10 border border-error/30 rounded-lg animate-slideDown"
            role="alert"
          >
            <BsExclamationCircleFill
              className="text-error shrink-0"
              size={20}
            />
            <p className="text-sm font-raleway text-error flex-1">
              {errorMessage}
            </p>
          </div>
        )}

        <div className="flex flex-col gap-5">
          <Input
            {...register("email")}
            icon="email"
            type="email"
            required
            placeholder="E-mail"
          />
          <Input
            {...register("password")}
            icon="password"
            type="password"
            required
            placeholder="Password"
            showPassword
          />
          <div className="flex items-center justify-between ml-auto">
            {/* <span className="flex items-center">
              <input type="checkbox" />
              <p className="font-raleway ml-2 max-md:text-xs">Remember me</p>
            </span> */}
            <Link
              className="text-azulfy-blue font-raleway hover:underline max-md:text-xs"
              href={"/forget-password"}
            >
              Forgot your password?
            </Link>
          </div>
        </div>

        <LinearButton
          disabled={isSubmitting}
          type="submit"
          text="Login"
          isLoading={isSubmitting}
          className="[&>div]:flex [&>div]:items-center [&>div]:mb-px"
        />
      </form>
    </div>
  );
}
